/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import styled from '@emotion/styled';
import { rgba, darken } from 'polished';

import { green } from '~/theseus/styles/core';

const toggleStylesWithDimensions = (width: number, height: number) => css`
  height: ${height}px;
  width: ${width}px;

  span {
    height: ${height - 4}px;
    width: ${height - 4}px;
  }

  &[aria-pressed="true"] {
    span { 
      left: ${width - height + 1}px;
    }

    &:hover > span {
      left: ${width - height - 2}px;
    }
  }
`;

interface ToggleProps {
  color?: string;
  size?: 'sm' | 'md';
  height?: number;
  width?: number;
}

const sizeStyles = ({ size, height, width }: ToggleProps) => {
  if (width && height) {
    return toggleStylesWithDimensions(width, height);
  }

  switch (size) {
    case 'md': return toggleStylesWithDimensions(60, 32);
    case 'sm':
    default: return toggleStylesWithDimensions(40, 22);
  }
};

const animations = css`transition: all 120ms linear;`;

const colorStyles = ({ color = darken(0.1, green) }: ToggleProps) => css`
  color: ${color};

  &[aria-pressed="true"] {
    background: ${color};
  }
`;

const BaseComponent = styled.button<ToggleProps>`
  position: relative;
  display: flex;
  background: ${rgba('#000', 0.2)};
  border-radius: 100px;
  cursor: pointer;
  border: none;
  ${animations}
  
  span {
    ${animations}
    position: absolute;
    top: 2px;
    border-radius: 50%;
    left: 3px;

    background: white;
  }
  
  &:hover > span {
    left: 5px;
  }

  ${colorStyles}
  ${sizeStyles}
`;

interface Props extends ToggleProps {
  isEnabled: boolean;
  label: string
}

const ToggleSwitch: React.SFC<Props & React.ButtonHTMLAttributes<HTMLButtonElement>> = (props) => {
  const {
    isEnabled,
    label,
    ...rest
  } = props;

  return (
    <BaseComponent
      type="button"
      aria-label={label}
      aria-pressed={isEnabled}
      {...rest}
    >
      <span />
    </BaseComponent>
  );
};

export default ToggleSwitch;
